/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { BlockProps } from '@/components/Block/Block';
import P from '@/components/Redactie/P/P';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import {
    mdiCheckboxMarkedCircle,
    mdiCheckboxBlankCircleOutline,
    mdiCheckCircle,
    mdiCheckAll,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ListEditor from '@/components/Redactie/ListEditor/ListEditor';
import { Switch } from '@headlessui/react';
import TextButton from '@/components/Button/TextButton';
import { motion, useReducedMotion } from 'framer-motion';
import EmojiText from '@/components/EmojiText/EmojiText';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';

import styles from './ChecklistBlock.module.sass';

interface ChecklistBlockData {
    title: string;
    items: Array<ChecklistBlockItem>;
}

interface ChecklistBlockLocalData {
    checkedItemIndexes: number[];
}

interface ChecklistBlockItem {
    text: string;
    is_mandatory: boolean;
}

const ChecklistBlock = ({
    subjectId,
    id,
    data,
    uuid,
    updatedAt,
    isEditable,
    isEditorPreview,
    isAnimated,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<ChecklistBlockData>({
        title: data.title ?? 'Voorbereiden',
        items: data.items ?? [
            { text: 'Dit', is_mandatory: true },
            { text: 'Dat', is_mandatory: true },
        ],
    });

    if (isEditable)
        return (
            <ChecklistBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                onEdited={onEdited}
            />
        );
    else
        return (
            <ChecklistBlockViewer
                subjectId={subjectId}
                id={id}
                blockData={blockData}
                setBlockData={setBlockData}
                updatedAt={updatedAt}
                isEditorPreview={isEditorPreview}
                isAnimated={isAnimated}
                onContentHeightChanged={onContentHeightChanged}
                onFinished={onFinished}
            />
        );
};

const ChecklistBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: ChecklistBlockData;
    setBlockData: (data: ChecklistBlockData) => void;
    onEdited: BlockProps['onEdited'];
}) => {
    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer className="bg-amber-200">
            <div>
                <P
                    isEditable
                    className="font-bold"
                    onChange={(value) => {
                        setBlockData({
                            ...blockData,
                            title: value,
                        });
                    }}
                    placeholder="Checklist titel"
                    value={blockData.title}
                    rows={1}
                />
            </div>
            <div className="mx-auto p-1">
                <ListEditor<ChecklistBlockItem>
                    list={blockData.items}
                    onSaved={(list) => {
                        setBlockData({
                            ...blockData,
                            items: list,
                        });
                    }}
                    createNewItem={() => {
                        return {
                            text: '',
                            is_mandatory: true,
                        };
                    }}
                    validateItem={(item) => ({
                        isValid: item.text.length > 0,
                        validationText: 'Vul een tekst in',
                    })}
                    validateList={(list) => ({
                        isValid:
                            list.filter((item) => item.is_mandatory).length > 0,
                        validationText: 'Voeg minimaal 1 verplicht item toe',
                    })}
                    listDescription={`Checklist "${blockData.title}"`}
                    listDisplayName="Checklist"
                    listItemDisplayName="Item"
                    minItems={1}
                    maxItems={10}
                    listSummaryItemData={(item) => ({
                        displayName: item.text,
                        icon: undefined,
                    })}
                    listItemTemplate={ChecklistBlockItemEditor}
                />
            </div>
        </BlockEditorContainer>
    );
};

const ChecklistBlockItemEditor = (
    index: number,
    item: ChecklistBlockItem,
    setItem: (item: ChecklistBlockItem) => void,
) => (
    <div className="rounded-md bg-white outline outline-2 outline-slate-200">
        <P
            isEditable
            maxLength={96}
            onChange={(value) => {
                setItem({
                    ...item,
                    text: value,
                    is_mandatory: value?.length > 0 ? item.is_mandatory : false,
                });
            }}
            placeholder={`Antwoord ${index + 1}`}
            value={item.text}
            rows={1}
        >
            {item.text}
        </P>
        <div className="mx-2">
            <FormControlLabel
                value="end"
                onChange={(event) =>
                    setItem({
                        ...item,
                        is_mandatory:
                            item?.text?.length === 0
                                ? false
                                : (event.target as HTMLInputElement).checked,
                    })
                }
                control={
                    <Checkbox
                        size="small"
                        disabled={item?.text?.length === 0}
                        checked={item.is_mandatory}
                    />
                }
                label={<span className="text-sm font-bold">Verplicht</span>}
                labelPlacement="end"
            />
        </div>
    </div>
);

const ChecklistBlockViewer = ({
    subjectId,
    id,
    blockData,
    updatedAt,
    isEditorPreview,
    isAnimated,
    onContentHeightChanged,
    onFinished,
}: {
    subjectId: BlockProps['subjectId'];
    id: BlockProps['id'];
    blockData: ChecklistBlockData;
    setBlockData: (data: ChecklistBlockData) => void;
    updatedAt: BlockProps['updatedAt'];
    isEditorPreview: BlockProps['isEditorPreview'];
    isAnimated: BlockProps['isAnimated'];
    onContentHeightChanged: BlockProps['onContentHeightChanged'];
    onFinished: BlockProps['onFinished'];
}) => {
    const reduceMotion = useReducedMotion();

    const {
        userDataLocal,
        setUserDataLocal,
    } = useBlockLocalUserData<ChecklistBlockLocalData>(
        subjectId,
        id,
        updatedAt,
        isEditorPreview,
        {
            checkedItemIndexes: [],
        },
    );

    const checkedAllMandatoryItems = useMemo(() => {
        return (
            userDataLocal.checkedItemIndexes.length ===
                blockData.items.length ||
            userDataLocal.checkedItemIndexes.filter(
                (index) => blockData.items[index].is_mandatory,
            ).length >=
                blockData.items.filter((item) => item.is_mandatory).length
        );
    }, [blockData, userDataLocal, userDataLocal.checkedItemIndexes]);

    const [hasFinished, setHasFinished] = useState(checkedAllMandatoryItems);

    useEffect(() => {
        if (checkedAllMandatoryItems) {
            setHasFinished(true);
            onFinished?.();
        }
    }, []);

    useEffect(() => {
        onContentHeightChanged?.();
    }, [checkedAllMandatoryItems]);

    return (
        <section
            aria-label="Checklist"
            className={`${styles['checklistblock']} ${
                isAnimated ? styles['checklistblock--animated'] : ''
            } ${hasFinished ? styles['checklistblock--finished'] : ''}`}
        >
            <div
                className={`${styles['checklistblock__header']} ${
                    hasFinished ? styles['checklistblock__header--picked'] : ''
                }`}
            >
                <Icon path={mdiCheckAll} size={1} />
                <p className={styles['checklistblock__header__question']}>
                    Checklist
                </p>
            </div>
            <div
                aria-label="Checklist"
                className={`${styles['checklistblock__container']} ${
                    hasFinished
                        ? styles['checklistblock__container--finished']
                        : ''
                }`}
            >
                <p className="mx-8 pt-1 text-center text-xl font-bold">
                    <EmojiText>{blockData.title}</EmojiText>
                </p>
                <ol className="grid gap-2 p-2 bg-white rounded-lg rounded-tr-sm shadow-md">
                    {blockData.items.map((item, index) => {
                        const isChecked = userDataLocal.checkedItemIndexes.includes(
                            index,
                        );
                        const setChecked = (checked: boolean) => {
                            if (checked) {
                                setUserDataLocal({
                                    ...userDataLocal,
                                    checkedItemIndexes: [
                                        ...userDataLocal.checkedItemIndexes,
                                        index,
                                    ],
                                });
                            } else {
                                setUserDataLocal({
                                    ...userDataLocal,
                                    checkedItemIndexes: userDataLocal.checkedItemIndexes.filter(
                                        (i) => i !== index,
                                    ),
                                });
                            }
                        };
                        return (
                            <motion.li
                                key={index}
                                whileHover={
                                    !hasFinished && !reduceMotion
                                        ? {
                                              scale: 1.04,
                                          }
                                        : undefined
                                }
                                whileTap={
                                    !hasFinished && !reduceMotion
                                        ? {
                                              scale: 0.95,
                                          }
                                        : undefined
                                }
                            >
                                <Switch.Group>
                                    <div
                                        className={`group flex items-center gap-1 w-full p-2 rounded-full transition-all ${
                                            hasFinished
                                                ? ''
                                                : 'hover:bg-yellow-200 hover:text-yellow-900 cursor-pointer'
                                        }`}
                                        onClick={
                                            hasFinished
                                                ? undefined
                                                : () => setChecked(!isChecked)
                                        }
                                    >
                                        <Switch
                                            checked={isChecked}
                                            disabled={hasFinished}
                                            onChange={setChecked}
                                            className="shrink-0 rounded-full"
                                        >
                                            <Icon
                                                path={
                                                    userDataLocal.checkedItemIndexes.includes(
                                                        index,
                                                    )
                                                        ? mdiCheckboxMarkedCircle
                                                        : mdiCheckboxBlankCircleOutline
                                                }
                                                size={1}
                                            />
                                        </Switch>
                                        <Switch.Label
                                            className={`relative me-4 text-lg text-balance font-semibold px-1 ${
                                                hasFinished
                                                    ? ''
                                                    : 'cursor-pointer'
                                            } ${
                                                isChecked
                                                    ? 'line-through decoration-current hover:decoration-black/10 transition-all duration-1000'
                                                    : ''
                                            }`}
                                        >
                                            <EmojiText>{item.text}</EmojiText>
                                        </Switch.Label>
                                    </div>
                                </Switch.Group>
                            </motion.li>
                        );
                    })}
                </ol>
                {!hasFinished && checkedAllMandatoryItems && (
                    <TextButton
                        icon={mdiCheckCircle}
                        onClick={() => {
                            onFinished?.();
                            setHasFinished(true);
                        }}
                        isSmall
                        className="bg-yellow-800"
                    >
                        Opslaan
                    </TextButton>
                )}
            </div>
        </section>
    );
};

export default ChecklistBlock;
